import type { Choice, Reflection, Route, Stage, WeekCase } from '../types/filtek'
import { imagery } from './imagery'
import { brand } from './palette'

export const routeOptions: {
  id: Route
  index: string
  label: string
  detail: string
  accent: string
}[] = [
  {
    id: 'new',
    index: '01',
    label: 'New to Filtek',
    detail: 'Discover what one restorative family can cover.',
    accent: brand.sky
  },
  {
    id: 'better',
    index: '02',
    label: 'Looking for something better',
    detail: 'Tell us where your current solution compromises.',
    accent: brand.green
  },
  {
    id: 'existing',
    index: '03',
    label: 'Already using Filtek',
    detail: "See if you're getting the full value of the family.",
    accent: brand.easyMatch
  }
]

export const flows: Record<Route, Stage[]> = {
  better: ['better-priority', 'better-mirror', 'reveal', 'needs', 'recommend', 'family', 'deeper'],
  new: ['new-priority', 'new-bridge', 'needs', 'week', 'recommend', 'family', 'deeper'],
  existing: [
    'existing-usage',
    'existing-competitor',
    'existing-closer',
    'needs',
    'recommend',
    'family',
    'deeper'
  ]
}

/** "Looking for something better" — improvement priorities and the reflection each triggers. */
export const improvementPriorities: Reflection[] = [
  {
    id: 'aesthetic-results',
    label: 'Aesthetic results',
    need: 'aesthetic',
    emphasis: 'aesthetic control',
    mirror: 'Great aesthetics shouldn’t have to mean greater complexity.',
    support: 'Layering, translucency and polish are craft. The material should carry some of that load.'
  },
  {
    id: 'shade-selection',
    label: 'Shade selection',
    need: 'everyday',
    emphasis: 'shade confidence',
    mirror: 'Everyday restorations shouldn’t require an everyday shade puzzle.',
    support: 'Most restorations live in a narrow band of the guide. Thirty options rarely make that easier.'
  },
  {
    id: 'handling',
    label: 'Handling',
    need: 'handling',
    emphasis: 'handling',
    mirror: 'A material should follow your hand — not argue with it.',
    support: 'Sculpt, adapt, hold. Handling is where minutes quietly disappear.'
  },
  {
    id: 'speed',
    label: 'Speed & efficiency',
    need: 'posterior',
    emphasis: 'efficiency',
    mirror: 'Saving time shouldn’t mean compromising somewhere else.',
    support: 'Fewer increments only count if the restoration is just as good at the end.'
  },
  {
    id: 'posterior-workflow',
    label: 'Posterior workflow',
    need: 'posterior',
    emphasis: 'posterior efficiency',
    mirror: 'The back of the mouth shouldn’t be where your standards drop.',
    support: 'Depth of cure, access, adaptation. Posterior cases have their own set of demands.'
  },
  {
    id: 'simplicity',
    label: 'Simplicity',
    need: 'everyday',
    emphasis: 'simplicity',
    mirror: 'The restorative system should simplify your work — not add another decision.',
    support: 'Simplicity isn’t fewer features. It’s fewer decisions at the chair.'
  },
  {
    id: 'fewer-materials',
    label: 'Fewer materials to manage',
    need: 'everyday',
    emphasis: 'a smaller material set',
    mirror: 'More boxes on the shelf rarely means more control in the chair.',
    support: 'Every extra brand brings its own technique, shade logic and inventory.'
  }
]

export const resonanceOptions: Choice[] = [
  { id: 'yes', label: 'Yes', detail: 'That is exactly it.' },
  { id: 'sometimes', label: 'Sometimes', detail: 'Depends on the case.' },
  { id: 'not-really', label: 'Not really', detail: 'Something else bothers me more.' }
]

export const resonanceResponse: Record<string, string> = {
  yes: 'Then it is worth questioning the material — not your technique.',
  sometimes: 'Which is usually a sign that one material is being asked to cover too much.',
  'not-really': 'Fair. The point still stands: no single composite is ideal for every case.'
}

/** "New to Filtek" — what matters most when choosing a restorative system. */
export const selectionPriorities: Choice[] = [
  {
    id: 'aesthetics',
    label: 'Aesthetics',
    need: 'aesthetic',
    emphasis: 'aesthetics',
    detail: 'Shade, translucency, polish, invisibility.'
  },
  {
    id: 'simplicity',
    label: 'Simplicity',
    need: 'everyday',
    emphasis: 'simplicity',
    detail: 'Fewer decisions, fewer steps, less to think about.'
  },
  {
    id: 'versatility',
    label: 'Versatility',
    need: 'everyday',
    emphasis: 'versatility',
    detail: 'One material that behaves well front and back.'
  },
  {
    id: 'efficiency',
    label: 'Efficiency',
    need: 'posterior',
    emphasis: 'efficiency',
    detail: 'Time per restoration, without cutting corners.'
  },
  {
    id: 'predictability',
    label: 'Predictability',
    need: 'handling',
    emphasis: 'predictability',
    detail: 'The same handling and the same result, case after case.'
  }
]

/** "Already using Filtek" — how much of the family is in use. */
export const usageLevels: Choice[] = [
  { id: 'one', label: 'One of them', detail: 'My go-to for one type of case.' },
  { id: 'two', label: 'Two of them', detail: 'Two Filtek materials, used regularly.' },
  { id: 'all', label: 'All three', detail: 'Filtek covers the majority of my restorative work.' }
]

export const usageResponse: Record<string, string> = {
  one: 'Most clinicians start exactly there — with the one case Filtek solved first.',
  two: 'That already covers a lot of the week. There is one member left, and a cousin.',
  all: 'Then this is about the cousin most clinicians have never been shown.'
}

export const competitorAreas: Choice[] = [
  {
    id: 'anterior',
    label: 'Anterior aesthetics',
    need: 'aesthetic',
    detail: 'Visible, layered, shade-critical cases.'
  },
  {
    id: 'everyday',
    label: 'Everyday restorations',
    need: 'everyday',
    detail: 'The bread-and-butter work.'
  },
  {
    id: 'posterior',
    label: 'Posterior / bulk fill',
    need: 'posterior',
    detail: 'Class I and II, depth and speed.'
  },
  {
    id: 'flowable',
    label: 'Flowable work',
    need: 'handling',
    detail: 'Bases, liners, tight access and adaptation.'
  },
  {
    id: 'complex-shade',
    label: 'Complex shade situations',
    need: 'everyday',
    detail: 'When the guide and the tooth disagree.'
  },
  {
    id: 'other',
    label: 'Somewhere else',
    need: 'handling',
    detail: 'Another part of the workflow entirely.'
  },
  { id: 'nowhere', label: 'Nowhere', detail: 'Filtek already covers my cases.' }
]

export const weekCases: WeekCase[] = [
  {
    id: 'class-ii',
    situation: 'Class II on a lower molar, 4 mm deep',
    frequency: 'Several times a week',
    need: 'posterior',
    coverage: 'Filtek Bulk in one increment — sculpt the marginal ridge, cure once.',
    image: imagery.posterior
  },
  {
    id: 'central',
    situation: 'Single central incisor, shade critical',
    frequency: 'Once or twice a month',
    need: 'aesthetic',
    coverage: 'Filtek Supreme layered with dentin and enamel opacities for depth and halo.',
    image: imagery.anterior
  },
  {
    id: 'premolar',
    situation: 'Small occlusal on a premolar',
    frequency: 'Most days',
    need: 'everyday',
    coverage: 'Filtek Easy Match in one of three options — sculpt, cure, polish.',
    image: imagery.layers
  },
  {
    id: 'class-v',
    situation: 'Cervical Class V near the gingival margin',
    frequency: 'Weekly',
    need: 'handling',
    coverage: 'Warmed composite adapts into a tight, curved prep without slumping.',
    image: imagery.flow
  },
  {
    id: 'deep-box',
    situation: 'Deep proximal box with awkward access',
    frequency: 'Weekly',
    need: 'handling',
    coverage: 'Pre-warmed Filtek Bulk flows into the box, then sculpts as normal.',
    image: imagery.flow
  },
  {
    id: 'uncertain-shade',
    situation: 'Two restorations, uncertain shade',
    frequency: 'Most days',
    need: 'everyday',
    coverage: 'One of three Easy Match options, blending into the surrounding tooth.',
    image: imagery.enamel
  }
]

export type CtaKind = 'technique' | 'product' | 'evidence' | 'case' | 'contact'

export const ctaOptions: { id: CtaKind; label: string; detail: string }[] = [
  { id: 'technique', label: 'Explore the technique', detail: 'Step sequence, chairside' },
  { id: 'product', label: 'See the product', detail: 'Specification and shades' },
  { id: 'evidence', label: 'View clinical evidence', detail: 'Published performance data' },
  { id: 'case', label: 'Watch a clinical case', detail: 'Filmed, start to polish' },
  { id: 'contact', label: 'Talk to a Solventum representative', detail: 'Practice-level conversation' }
]

export const deeperSections: { id: string; label: string; items: string[] }[] = [
  {
    id: 'products',
    label: 'Products',
    items: [
      'Solventum Filtek Supreme',
      'Solventum Filtek Easy Match',
      'Solventum Filtek Bulk',
      'Solventum Filtek Composite Warmer'
    ]
  },
  {
    id: 'techniques',
    label: 'Techniques',
    items: [
      'Anterior stratification with dentin and enamel opacities',
      'Single-shade selection protocol',
      'Bulk placement to 5 mm',
      'Pre-warmed composite placement',
      'Finishing and polishing sequence'
    ]
  },
  {
    id: 'cases',
    label: 'Clinical cases',
    items: [
      'Diastema closure, upper centrals',
      'Class II with a deep proximal box',
      'Cervical Class V restoration',
      'Full posterior quadrant in one appointment'
    ]
  },
  {
    id: 'evidence',
    label: 'Evidence',
    items: [
      '10-year follow-up: nanofiller gloss retention',
      'Shrinkage stress and marginal integrity at 5 mm',
      'Shade-match acceptability from three options',
      'Viscosity and cavity adaptation in pre-warmed composite'
    ]
  },
  {
    id: 'videos',
    label: 'Videos',
    items: [
      'The Filtek family in five minutes',
      'Shade selection, simplified',
      'Posterior workflow, filmed chairside',
      'Using the Composite Warmer chairside'
    ]
  }
]
